import React, { useMemo } from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  PickersDay,
  type PickersDayProps,
} from "@mui/x-date-pickers/PickersDay";
import { Dayjs } from "dayjs";
import { DATE_FORMAT, toDateString } from "./venueHireUtils";

type HighlightColor = "success" | "primary" | "info";

type CalendarDayProps = PickersDayProps<Dayjs> & {
  highlightedDates: Record<string, boolean>;
  highlightColor: HighlightColor;
};

const colorStyles: Record<
  HighlightColor,
  { bg: string; hover: string; text?: string }
> = {
  success: { bg: "success.light", hover: "success.main", text: "success.contrastText" },
  primary: { bg: "primary.light", hover: "primary.main", text: "primary.contrastText" },
  info: { bg: "info.light", hover: "info.main", text: "info.contrastText" },
};

const CalendarDay = ({
  highlightedDates,
  highlightColor,
  day,
  outsideCurrentMonth,
  ...other
}: CalendarDayProps) => {
  const dateStr = day.format(DATE_FORMAT);
  const highlighted = highlightedDates[dateStr];
  const colors = colorStyles[highlightColor];

  return (
    <PickersDay
      {...other}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      sx={{
        ...(highlighted && {
          fontWeight: 700,
          bgcolor: colors.bg,
          ...(colors.text ? { color: colors.text } : {}),
          "&:hover, &:focus": {
            bgcolor: colors.hover,
          },
          "&.Mui-selected": {
            bgcolor: "primary.main",
            color: "primary.contrastText",
            "&:hover, &:focus": {
              bgcolor: "primary.dark",
            },
          },
        }),
      }}
    />
  );
};

const renderCalendarDay =
  (highlightedDates: Record<string, boolean>, highlightColor: HighlightColor) =>
  (props: PickersDayProps<Dayjs>) =>
    (
      <CalendarDay
        {...props}
        highlightedDates={highlightedDates}
        highlightColor={highlightColor}
      />
    );

export interface VenueHireDateCalendarProps {
  value: Dayjs | null;
  onChange: (date: Dayjs | null) => void;
  highlightedDates: Record<string, boolean>;
  minDate: Dayjs;
  maxDate: Dayjs;
  shouldDisableDate?: (date: Dayjs) => boolean;
  highlightColor?: HighlightColor;
  legendLabel?: string;
  legendCount?: number;
}

const VenueHireDateCalendar: React.FC<VenueHireDateCalendarProps> = ({
  value,
  onChange,
  highlightedDates,
  minDate,
  maxDate,
  shouldDisableDate,
  highlightColor = "success",
  legendLabel = "Slots available",
  legendCount,
}) => {
  const DaySlot = useMemo(
    () => renderCalendarDay(highlightedDates, highlightColor),
    [highlightedDates, highlightColor],
  );

  const highlightedDayCount =
    legendCount ??
    Object.values(highlightedDates).filter(Boolean).length;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          maxWidth: 360,
          mx: { xs: "auto", md: 0 },
        }}
      >
        <DateCalendar
          value={value}
          onChange={onChange}
          minDate={minDate}
          maxDate={maxDate}
          shouldDisableDate={shouldDisableDate}
          slots={{ day: DaySlot }}
        />
        <Stack
          direction="row"
          spacing={2}
          sx={{ px: 2, pb: 2 }}
          flexWrap="wrap"
          useFlexGap
        >
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                bgcolor: `${highlightColor}.light`,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {legendLabel}
            </Typography>
          </Stack>
          {highlightedDayCount > 0 && (
            <Typography variant="caption" color="text.secondary">
              {highlightedDayCount} day{highlightedDayCount === 1 ? "" : "s"}
            </Typography>
          )}
        </Stack>
      </Paper>
    </LocalizationProvider>
  );
};

export const datesToHighlightMap = (
  dates: string[],
): Record<string, boolean> =>
  dates.reduce<Record<string, boolean>>((acc, date) => {
    acc[date] = true;
    return acc;
  }, {});

export const defaultShouldDisableUnlessHighlighted =
  (highlightedDates: Record<string, boolean>) =>
  (date: Dayjs): boolean =>
    !highlightedDates[toDateString(date)];

export default VenueHireDateCalendar;
